import React from 'react';

import { Button, Checkbox } from '@poseidon/components/Input2';
import Popup from '@poseidon/components/Popup/Popup';
import { TouchpointSwimlanesPure } from '@poseidon/components/TouchpointSwimlanes/TouchpointSwimlanesPure';

import { updateWithToast } from '@/clientOnlyUtils/clientUtils';
import { api } from '@/utils/api';

type Props = {
  /** public team id */
  teamId: string;
  /** contact_public_id */
  contactId: string;
  /** userTeam.touchpointProgramsEnabled — hides the whole section when off */
  enabled: boolean;
};

/**
 * Contact-detail section for touch-point programs. Renders the contact's active
 * program as horizontal swimlanes, or an "Add touch points" affordance when the
 * contact isn't in one yet. Entirely hidden unless the team has the flag on.
 */
export function TouchpointProgramSection({ teamId, contactId, enabled }: Props) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());

  const utils = api.useUtils();
  const programQuery = api.program.getForContact.useQuery(
    { teamId, contactId },
    { enabled: enabled && Boolean(contactId) },
  );
  const templatesQuery = api.cadenceTemplate.names.useQuery(
    { teamId, pagination: { limit: 200, offset: 0 } },
    { enabled: enabled && modalOpen },
  );
  const attachMutation = api.program.attachTouchpoints.useMutation();

  if (!enabled) return null;

  const enrollment = programQuery.data?.enrollment ?? null;
  const columns = programQuery.data?.columns ?? [];
  const hasProgram = enrollment != null;

  const onAttach = async () => {
    if (selected.size === 0) return;
    await updateWithToast({
      loading: 'Attaching touch points…',
      success: 'Touch points attached',
      mutationFnAsync: () =>
        attachMutation.mutateAsync({
          teamId,
          contactId,
          touchpoints: [...selected].map((cadenceTemplatePublicId) => ({ cadenceTemplatePublicId })),
        }),
    });
    setModalOpen(false);
    setSelected(new Set());
    await utils.program.getForContact.invalidate({ teamId, contactId });
  };

  return (
    <div data-testid="touchpoint-program-section" className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Touch points</span>
        {!hasProgram && (
          <Button size="xs" color="blue" onClick={() => setModalOpen(true)} data-testid="add-touch-points-btn">
            Add touch points
          </Button>
        )}
      </div>

      {hasProgram && (
        <TouchpointSwimlanesPure
          programName={enrollment.programName}
          status={enrollment.status}
          columns={columns}
        />
      )}
      {!hasProgram && (
        <span className="text-sm text-gray-500">
          Run several weekly channels in parallel on this contact (email, call, LinkedIn…). Any reply stops all of them.
        </span>
      )}

      {modalOpen && (
        <Popup onClose={() => setModalOpen(false)} className="w-[420px] max-w-[90vw] p-4">
          <div className="flex flex-col gap-3">
            <span className="text-base font-medium">Add touch points</span>
            <span className="text-sm text-gray-500">
              Pick up to 3 single-type weekly templates. Each becomes a parallel channel.
            </span>

            <div className="flex max-h-72 flex-col gap-1 overflow-y-auto">
              {templatesQuery.isLoading && <span className="text-sm text-gray-500">Loading templates…</span>}
              {templatesQuery.data?.data.length === 0 && (
                <span className="text-sm text-gray-500">No templates yet — create a single-type weekly template first.</span>
              )}
              {templatesQuery.data != null && templatesQuery.data.data.length > 0 && (
                <Checkbox
                  options={templatesQuery.data.data.map((t) => ({ value: t.id, label: t.name }))}
                  value={[...selected]}
                  onChange={(vals) => setSelected(new Set(vals.slice(0, 3)))}
                />
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button size="sm" tone="outline" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                color="blue"
                disabled={selected.size === 0 || attachMutation.isPending}
                onClick={() => void onAttach()}
                data-testid="attach-touch-points-confirm">
                Attach {selected.size > 0 ? `(${selected.size})` : ''}
              </Button>
            </div>
          </div>
        </Popup>
      )}
    </div>
  );
}

export default TouchpointProgramSection;
